import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Check, ChevronRight, Clock, HelpCircle, KeyRound, Wallet } from "lucide-react";
import { Button, Textarea } from "@ncoder/ui";
import { cn } from "@/lib/utils";
import type { ClarifyAnswer, Interaction } from "@/services/multi-agent/protocol";
import type { RespondBody } from "@/services/multi-agent/interactions";
import {
  canSubmit,
  copyFor,
  expiredLine,
  isClarifyLike,
  optionRequiresText,
  optionRequiresConfirm,
  sendsOnPress,
  CONTINUE_BUILT_OPTION_ID,
  consequenceForOption,
  ACK_OPTION_ID,
  submitVerb,
  expiryLine,
  msUntilExpiry,
  preselectedOptionId,
  preselectedQuestionOptionId,
  requiresOrgNameConfirmation,
  holdsRunUp,
  standingLine,
  visibleDetail,
  type AskOwner,
  type InteractionTone,
} from "@/services/multi-agent/interactionCopy";
import { FREE_TEXT_MAX, OVERRIDE_REASON_MAX } from "@/services/multi-agent/interactions";
import { InteractionBody, inputComplete, schemaFields } from "./InteractionBody";
import { ClarifyQuestions, candidatesAsOptions } from "./ClarifyQuestions";
import { OPTION_APPROVE } from "@/services/multi-agent/laneV2/types";
import { OptionCards } from "./OptionCards";

/**
 * One interaction, rendered inline in the transcript. One component, eight kinds.
 *
 * NOT A MODAL, and that is a correctness decision rather than a style one. Every interaction carries
 * a `context_ref` pointing at the plan, step, component or changeset it is about (§2.16) — a modal
 * covers exactly the thing the reader needs to look at in order to answer. So the card sits where the
 * question was asked, and selecting it asks the caller to reveal its `context_ref` target.
 *
 * ONE COMPONENT, NOT EIGHT. The eight kinds share the whole frame: heading, the standing line, the
 * consequence sentence, the deadline, the options, free text, the submit path, focus handling and the
 * live region. Only the BODY differs, and three of the eight have no body at all. Eight components
 * would be eight copies of the parts that are easy to get wrong and one copy each of the parts that
 * are not.
 *
 * WHAT IT REFUSES TO DO:
 *
 *   - It never derives `blocking` from `kind`. `standingLine` reads the frame (§5.4 ¹²).
 *   - It never auto-submits. The recommended option is pre-selected; submitting is always a click.
 *   - It never renders a bare "No" for a closed question. A resolved card collapses to what was
 *     chosen and STAYS in the transcript, because the thread is the record of a decision — a question
 *     that vanishes makes the run unreadable an hour later.
 *   - It computes no legality. Whether an action is offered at all comes from `state.actions`; this
 *     card only asks the question the server raised.
 *
 * The `edits` payload is the caller's, not this component's: it is per-kind (a `Plan`, or components
 * plus hunks, or tool args, or a number), and a switch over that here would be a second copy of a
 * rule the server owns. This card produces `{option_id?, free_text?, override_reason?}` and lets the
 * caller merge whatever `edits` its surface is responsible for.
 */

const TONE_ICON: Record<InteractionTone, typeof HelpCircle> = {
  ask: HelpCircle,
  approve: Check,
  danger: AlertTriangle,
  budget: Wallet,
  auth: KeyRound,
};

const TONE_ACCENT: Record<InteractionTone, string> = {
  ask: "border-l-primary",
  approve: "border-l-primary",
  // Amber/red is reinforced by the word "delete" in the heading and by the typed confirmation —
  // never colour alone (§16).
  danger: "border-l-destructive",
  budget: "border-l-warning-strong",
  auth: "border-l-warning-strong",
};

export interface InteractionCardProps {
  interaction: Interaction;
  /** Needed only for the production destructive rule; `null` while the snapshot is in flight. */
  org?: { is_production?: boolean; org_name?: string } | null;
  /**
   * Submit. Resolves when the server has answered; rejects with the client's typed error so the
   * caller can decide what a 409 means for its surface.
   */
  onRespond: (body: RespondBody) => Promise<void>;
  /** Reveal and highlight `context_ref`'s target. Omit where the surface has nothing to scroll to. */
  onRevealContext?: (ref: { kind?: string; id?: string }) => void;
  /** Rendered under the controls — the caller's place for a 409's explanation. */
  error?: string | null;
  /** The evidence gate's unmet list; presence turns on the override affordance. */
  unmetEvidence?: readonly { evidence_id: string; kind: string }[] | null;
  /** `RunState.usage` — the real figures the budget body reads. */
  usage?: import("@/services/multi-agent/protocol").Usage | null;
  /** `RunState.changeset` — where the destructive body finds what is being deleted. */
  changeset?: import("@/services/multi-agent/protocol").Changeset | null;
  /** The `tool_call` part an `approve_tool`'s `context_ref` names. */
  toolCall?: import("@/services/multi-agent/protocol").Part | null;
  /**
   * Open the plan editor — where `approve_plan`'s "Edit first" option actually leads.
   *
   * WHY THE CARD INTERCEPTS THIS OPTION INSTEAD OF SENDING IT. The backend routes every non-rejecting
   * `approve_plan` answer through the same approval handler (`interactions.py` `_on_approve_plan`:
   * only `reject`/`cancel`/`deny`/`stop_here` count as a decline), so `{option_id:"edit"}` resolves
   * the question, returns a `resume_from` handle, and the route would START THE EXECUTE RUN — the
   * one outcome a reader pressing "Edit first" was trying to avoid. The plan is edited through
   * `PUT /api/runs/{id}/plan` (the pane's editor), which leaves this question pending for the
   * approval that follows. Omit it and the option submits as the server defines it.
   */
  onEditPlan?: () => void;
  /** Extra body between the consequence line and the options — for a caller with its own. */
  children?: React.ReactNode;
  /**
   * How the server's `prompt` is laid out. `heading` (default, unchanged): the whole prompt is the
   * heading. `full`: the FIRST line is the heading and every further line renders below it verbatim
   * (whitespace kept) — for lane-v2 asks, whose prompt carries the NOT INCLUDED lines and notes an
   * approver must read before approving.
   */
  promptLayout?: "heading" | "full" | "summary";
  /**
   * Options as BUTTONS that answer in one click, instead of radios plus a submit. For the lane-v2
   * decision card: "Approve plan v1" / "Decline" are the whole gesture. An option that needs words
   * with it (`changes_requested`) still selects and opens the free-text field, and the submit
   * button returns for that case only. Never auto-submits anything the reader did not press.
   */
  optionsAsActions?: boolean;
  /**
   * Who owns the question (`interactionCopy.ownerOf`). Default `run` — the old lane, unchanged. A
   * lane-v2 host passes `task`, so a shared kind (`provide_input`) never says "this run is paused".
   */
  owner?: AskOwner;
  className?: string;
}

/** "Approve this plan?" and "approve this plan" are the same sentence for the eyebrow's purposes. */
function sameSentence(a: string, b: string): boolean {
  const norm = (t: string) => t.trim().toLowerCase().replace(/[?.!:\s]+$/g, "");
  return norm(a) === norm(b);
}

export function InteractionCard({
  interaction,
  org,
  onRespond,
  onRevealContext,
  onEditPlan,
  error,
  unmetEvidence,
  usage,
  changeset,
  toolCall,
  children,
  className,
  promptLayout = "heading",
  optionsAsActions = false,
  owner = "run",
}: InteractionCardProps) {
  const copy = copyFor(interaction.kind, owner);
  const promptLines = promptLayout !== "heading" ? interaction.prompt.split("\n") : [interaction.prompt];
  const promptHeading = promptLines[0]?.trim() || interaction.prompt;
  const promptRest = promptLayout !== "heading" ? promptLines.slice(1).join("\n").trim() : "";
  const detail = visibleDetail(interaction);
  /*
   * ── THE SAME SENTENCE IS NOT SAID TWICE ─────────────────────────────────────────────────────
   *
   * Seen on a real `approve_plan`: eyebrow "Approve this plan?" over heading "Approve this plan?",
   * then the server's detail "Approving starts a new run that executes exactly these steps…" over
   * the client's consequence "Approving starts a new run that works from this plan…" — four lines
   * for two facts, on the card that most needs to read as ONE decision. The eyebrow exists to say
   * what the prompt does not (that this is a write decision); when the prompt already says it, the
   * eyebrow goes. And on an `approve_plan` the backend's own detail IS the consequence, so the
   * client's paraphrase yields to it. Everywhere else both stay: an `approve_changeset` detail is
   * "2 ApexClasses", which is context, not consequence.
   */
  const eyebrow =
    !isClarifyLike(interaction.kind) && !sameSentence(copy.title, promptHeading) ? copy.title : null;
  const baseConsequence =
    isClarifyLike(interaction.kind) || (interaction.kind === "approve_plan" && detail) ? null : copy.consequence;
  // Compact when every option is a bare label: three tall rows for "Approve · Edit · Reject" was
  // most of the card's height. Options with sub-copy keep their stacked cards.
  const optionLayout =
    interaction.options.length <= 4 && interaction.options.every((o) => !o.description) ? "row" : "stack";
  const Icon = TONE_ICON[copy.tone];

  /*
   * Contracts 0.2.19 `approve_blocked`: the server will refuse Approve on this pending approve_apply
   * (409 VALIDATION_NOT_PASSED). Approve is shown unavailable with the reason — never a clickable
   * Approve — while decline / request-changes stay selectable. Typed from the generated contract.
   */
  const approveBlocked: Interaction["approve_blocked"] =
    interaction.kind === "approve_apply" ? (interaction.approve_blocked ?? null) : null;
  const blockedOptionIds = approveBlocked ? [OPTION_APPROVE] : undefined;
  const [optionId, setOptionId] = useState<string | null>(() => {
    const seeded = preselectedOptionId(interaction);
    return seeded && approveBlocked && seeded === OPTION_APPROVE ? null : seeded;
  });
  const [freeText, setFreeText] = useState("");
  const consequence = consequenceForOption(baseConsequence, interaction.kind, optionId);
  /** A destructive option (park card "Cancel the task") needs a second, explicit press: selecting it sends nothing. */
  const needsConfirm = optionRequiresConfirm(optionId);
  /*
   * Contracts 0.2.9. A `candidates` pick is answered with `ref_id`; a batch's sub-questions with
   * `answers`. Both are seeded the way options are: the recommended/default choice is pre-selected
   * and NEVER auto-submitted. An option pick and a candidate pick are one answer said two ways, so
   * choosing either clears the other.
   */
  const [refId, setRefId] = useState<string | null>(null);
  const questions = interaction.questions ?? null;
  const isBatch = !!questions?.length;
  const [answers, setAnswers] = useState<Record<string, ClarifyAnswer>>(() => {
    const seeded: Record<string, ClarifyAnswer> = {};
    for (const q of questions ?? []) {
      const preset = preselectedQuestionOptionId(q);
      if (preset) seeded[q.question_id] = { option_id: preset };
    }
    return seeded;
  });
  const chooseOption = (id: string) => {
    if (blockedOptionIds?.includes(id)) return;
    setOptionId(id);
    setRefId(null);
  };
  const chooseCandidate = (id: string) => {
    setRefId(id);
    setOptionId(null);
  };
  const [orgNameTyped, setOrgNameTyped] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  /*
   * `provide_input`'s answer. Held here rather than in the body component because the CARD submits,
   * and a body that owned the values would have to lift them anyway.
   */
  const [inputValues, setInputValues] = useState<Record<string, unknown>>({});

  /*
   * The clock, ticking only while the deadline is close enough to matter.
   *
   * `expiryLine` is silent outside the last hour, so a 24h TTL costs no timer at all. Inside it, a
   * 30s interval is enough for a minute-granularity countdown and cheap enough not to think about.
   */
  const [now, setNow] = useState(() => Date.now());
  const remaining = msUntilExpiry(interaction.expires_at, now);
  const ticking = remaining !== null && remaining > 0 && remaining < 60 * 60 * 1000;
  useEffect(() => {
    if (!ticking) return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [ticking]);

  const deadline = expiryLine(interaction.expires_at, now);
  const isExpired = interaction.status === "expired" || (remaining !== null && remaining <= 0);
  const isResolved = interaction.status === "resolved";
  const isClosed = isExpired || isResolved || interaction.status === "cancelled";

  const needsOrgName = requiresOrgNameConfirmation(interaction.kind, org);
  const orgNameOk = !needsOrgName || orgNameTyped.trim() === (org?.org_name ?? "").trim();
  const gated = !!unmetEvidence?.length;
  const overrideOk = !gated || (overrideOpen && overrideReason.trim().length > 0);

  /*
   * `provide_input` is the one kind whose answer is neither an option nor free text: §5.4 gives it no
   * options at all, and `allow_free_text` may be false. So a completed form counts as answerable —
   * without this the submit button on the only kind that REQUIRES a form could never enable.
   */
  const formAnswered =
    interaction.kind === "provide_input" &&
    // A schema with no fields is vacuously "complete": a lane-v2 park card (options, no form) must not count as answered
    // before the option it needs words for (`describe_change`) has them.
    (schemaFields(interaction.input_schema).length > 0 || interaction.options.length === 0) &&
    inputComplete(interaction.input_schema, inputValues);

  const answerable = canSubmit(interaction, { optionId, freeText, refId, answers });
  /*
   * "Edit first" on an `approve_plan` is a detour, not an answer (see `onEditPlan`). When the host
   * can open the editor it is always pressable — nothing is sent, so nothing needs to be complete.
   */
  const editsPlan = interaction.kind === "approve_plan" && optionId === "edit" && !!onEditPlan;
  const ready =
    !busy &&
    !isClosed &&
    (editsPlan || ((answerable || formAnswered) && orgNameOk && overrideOk));

  /*
   * Focus moves to the card when a BLOCKING question opens, and only then.
   *
   * A blocking question has stopped the run, so the next useful keystroke belongs here — and a
   * screen-reader user needs to be told the run is waiting rather than discovering it by scrolling.
   * An advisory question must NOT steal focus: the run is still streaming and the reader may be
   * mid-sentence somewhere else. Once only, keyed on the interaction id, so a re-render does not
   * yank the caret back.
   */
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const focusedFor = useRef<string | null>(null);
  useEffect(() => {
    if (isClosed || !interaction.blocking) return;
    if (focusedFor.current === interaction.interaction_id) return;
    focusedFor.current = interaction.interaction_id;
    headingRef.current?.focus();
  }, [interaction.interaction_id, interaction.blocking, isClosed]);

  const chosenLabel = useMemo(() => {
    const chosen = interaction.options.find((o) => o.option_id === optionId);
    return chosen?.label ?? null;
  }, [interaction.options, optionId]);

  /** Send with a given option (the radio selection, or the action button just pressed). */
  const send = async (chosen: string | null) => {
    setBusy(true);
    try {
      await onRespond({
        ...(chosen ? { option_id: chosen } : {}),
        // Contracts 0.2.9: a candidate pick, or the batch's answers keyed by question_id.
        ...(refId ? { ref_id: refId } : {}),
        ...(isBatch && Object.keys(answers).length ? { answers } : {}),
        // Only for `provide_input`, and only when there is something to send — `input` on a kind
        // that does not expect it is a field the server did not ask for.
        ...(formAnswered ? { input: inputValues } : {}),
        ...(freeText.trim() && chosen !== CONTINUE_BUILT_OPTION_ID ? { free_text: freeText.trim().slice(0, FREE_TEXT_MAX) } : {}),
        ...(gated && overrideReason.trim()
          ? {
              override_reason: overrideReason.trim().slice(0, OVERRIDE_REASON_MAX),
              // L-28: an override must NAME what it waives — the exact ids this card is showing in
              // the checklist above. Sending the reason without them (or against a set the gate no
              // longer recognises) is refused typed rather than treated as blanket consent.
              override_unmet: unmetEvidence!.map((e) => e.evidence_id),
            }
          : {}),
      });
    } finally {
      setBusy(false);
    }
  };
  const submit = async () => {
    if (!ready) return;
    if (editsPlan) {
      onEditPlan?.();
      return;
    }
    await send(optionId);
  };
  /*
   * Action mode: an option that answers on its own is one click; one that needs words (or the
   * plan editor, or an override reason) selects itself and hands over to the usual path.
   */
  const actionMode = optionsAsActions && interaction.options.length > 0 && !isBatch && !interaction.candidates?.length;
  /*
   * Pills only when every label is a word or two (and there are few). A sentence-long option as a
   * pill is a ransom note: uneven widths, a filled one shouting, four of them wrapping. Those
   * render as a LIST of equal rows, the recommended one marked by a word rather than a fill — the
   * same rule the extension settled on, and what a reader can scan top to bottom.
   */
  const ACTION_PILL_MAX_CHARS = 32;
  const actionLayout: "pills" | "list" =
    interaction.options.length <= 3 && interaction.options.every((o) => o.label.length <= ACTION_PILL_MAX_CHARS && !o.description)
      ? "pills"
      : "list";
  const pressOption = async (id: string) => {
    if (busy) return;
    chooseOption(id);
    if (id === "edit" && onEditPlan) {
      onEditPlan();
      return;
    }
    if (sendsOnPress(interaction, id, gated)) await send(id);
  };

  /*
   * FREE TEXT IS ALWAYS AVAILABLE, NOT ALWAYS ON SCREEN. `allow_free_text` is true on nearly every
   * interaction, so a two-row textarea sat under every question — the biggest block of the "form"
   * reading, and unused whenever an offered option was right. Behind a disclosure it is one click
   * away. `<details>` rather than state: the browser's own semantics, keyboard-operable and
   * announced without ARIA of ours. When there are NO options the textarea is the only way to
   * answer, so it renders open and ABOVE the submit row rather than beside it.
   */
  const freeTextOnly = interaction.options.length === 0 && !interaction.candidates?.length;
  /*
   * `changes_requested` is not an answer without WHAT to change, so choosing it opens the text box
   * (and the submit stays off until it has text — `canSubmit`). `ack` (lane-v2 "carry on") takes
   * optional guidance, so the box is labelled as such.
   */
  const needsText = optionRequiresText(optionId);
  const freeTextLabel = needsText
    ? "What should change?"
    : optionId === ACK_OPTION_ID
      ? "Add guidance (optional)"
      : interaction.options.length
        ? "Answer in your own words"
        : "Your answer";
  const freeTextDisclosure =
    (interaction.allow_free_text || needsText) && !isBatch && optionId !== CONTINUE_BUILT_OPTION_ID ? (
      <details className="group min-w-0 open:basis-full" open={freeTextOnly || needsText}>
        <summary className="inline-flex cursor-pointer list-none items-center gap-1 text-[12px] font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <ChevronRight
            aria-hidden
            className="h-3 w-3 transition-transform group-open:rotate-90 motion-reduce:transition-none"
          />
          {freeTextLabel}
        </summary>
        <div className="mt-2">
        <label
          htmlFor={`ix-${interaction.interaction_id}-text`}
          className="sr-only"
        >
          {freeTextLabel}
        </label>
        <Textarea
          id={`ix-${interaction.interaction_id}-text`}
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          maxLength={FREE_TEXT_MAX}
          rows={2}
          disabled={busy}
          placeholder="Type an answer…"
          className="text-[13px]"
        />
        </div>
      </details>
    ) : null;

  const titleId = `ix-${interaction.interaction_id}-title`;

  // ── Closed: the record, kept in place ─────────────────────────────────────
  if (isClosed) {
    return (
      <section
        aria-labelledby={titleId}
        className={cn(
          "rounded-md border border-l-2 border-border border-l-border bg-muted/30 px-3.5 py-3",
          className,
        )}
      >
        <h4 id={titleId} className="text-[13px] font-semibold text-muted-foreground">
          {copy.title}
        </h4>
        <p className="mt-1 text-[13px] text-muted-foreground">
          {isResolved
            ? chosenLabel
              ? `Answered: ${chosenLabel}`
              : "Answered."
            : /*
               * An expired question is NOT a dead end. `resume_from` makes the same answer valid as a
               * NEW run, so the copy says what the user can still do rather than reporting a loss.
               */
              expiredLine(interaction.kind, owner)}
        </p>
      </section>
    );
  }

  // ── Open ──────────────────────────────────────────────────────────────────
  return (
    <section
      aria-labelledby={titleId}
      /*
       * NOT A CARD. A bordered, filled box inside a bordered conversation beside a bordered
       * workspace is the "traditional form dropped into a chat" reading — the thing this surface is
       * least supposed to be. What carries the meaning is the LEFT RULE, which is already
       * tone-coloured (destructive reads red, an ask reads accent), so the box around it was doing
       * nothing the rule was not.
       *
       * The question stays a `section` with its own accessible name, so nothing about assistive
       * navigation changes — only the paint.
       */
      className={cn("border-l-2 py-1 pl-3.5", TONE_ACCENT[copy.tone], className)}
    >
      {/*
        THE QUESTION IS THE HEADING, and this ordering is the change.
        
        It used to lead with four blocks before the question: a client-authored `copy.title`, an
        uppercase standing line, then the prompt, then the detail — so the thing the reader had to
        answer was the third paragraph, at the smallest weight of the three. That is the "giant
        technical form" reading rather than "Weave needs one decision from me".
        
        Now: a quiet eyebrow naming the KIND of decision (which the raw prompt often does not — a
        prompt can be "Which sandbox?" with no hint that approving writes to an org), the server's
        question at heading weight, then the detail. The standing and the deadline move into chips,
        which keeps both facts and stops them competing with the question for attention.
      */}
      <header className="flex flex-wrap items-start gap-x-2 gap-y-1.5">
        <Icon className="mt-[3px] h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        {/*
          `basis-48`: the question keeps at least ~12rem before the chips are allowed beside it, so
          on a phone the chips wrap UNDER the heading instead of pinning it to a one-word-per-line
          sliver (seen at 375px: "Which / object / should / carry…"). On a wide panel nothing changes
          — the row still fits, and `flex-1` takes the rest. Intrinsic, no breakpoint (§8.3).
        */}
        <div className="min-w-0 flex-1 basis-48">
          {/*
            THE EYEBROW EARNS ITS LINE OR IT GOES.
            
            For an approval it says what the prompt cannot: "Approve these changes?" above
            "Apply 2 components to this org?" tells the reader this is a write decision. For a
            `clarify` it restated the question's own form — "Which did you mean?" over "Which object
            should carry the approval routing?" — two interrogatives, one thought, and the reader's
            eye pays for both.
          */}
          {eyebrow && <p className="text-[12px] font-medium text-muted-foreground">{eyebrow}</p>}
          {/*
            `tabIndex={-1}` makes the heading programmatically focusable without putting it in the
            tab order — the target for the blocking-question focus move above. It is now the
            QUESTION, so a screen-reader user landing here hears what they are being asked.
            
            The server's own words, verbatim. Never parsed, never rewritten (§5.1).
          */}
          <h4
            id={titleId}
            ref={headingRef}
            tabIndex={-1}
            // `focus:outline-none`, not only `focus-visible`: the card focuses this heading from a
            // script on mount, and Chromium paints the UA focus ring for script focus when the
            // last input was not a pointer — a blue box around a sentence that is not a control.
            className="mt-0.5 text-[14.5px] font-semibold leading-snug text-foreground outline-none focus:outline-none focus-visible:outline-none"
          >
            {promptHeading}
          </h4>
        </div>
        <span className="flex shrink-0 flex-wrap items-center gap-1.5">
          {/*
            The run's standing, read off `blocking` and NEVER from `kind` (§5.4 ¹²). Same sentence
            as before, as a chip rather than an uppercase banner: `approve_changeset` is always
            advisory and `approve_plan` is blocking only in a write run, so this cannot be inferred
            from the kind and must be shown.
          */}
          {/* On a decision card (lane v2) the turn's story strip already says "Waiting for you";
              a second chip here was the "three statuses" problem. */}
          {!optionsAsActions && (
            <span
              className={cn(
                "inline-flex items-center rounded px-1.5 py-0.5 text-[11px]",
                holdsRunUp(interaction)
                  ? "bg-ma-warn-soft text-[var(--ma-state-warn-text)]"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {standingLine(interaction, owner)}
            </span>
          )}
          {deadline && (
            <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" aria-hidden />
              {deadline}
            </span>
          )}
        </span>
      </header>

      {/*
        `visibleDetail` hides a `detail` that is exactly the 0.2.9 producer's composed fallback —
        the why / impact / candidates / questions rendered structurally just below — so those facts
        are not printed twice. An author-written detail is always shown.
      */}
      {promptRest && promptLayout === "summary" ? (
        /*
         * `summary`: the question is the heading and what is decided is the caller's summary (the
         * plan's items, the package in a line); the server's own notes — CHANGE SHAPE, PRIMARY,
         * growth rules — stay one click away rather than standing between the reader and the buttons.
         */
        <details className="group mt-2 text-[12px]" data-interaction-notes="">
          <summary className="cursor-pointer list-none font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {/* the marker is hidden (`list-none`), so the arrow says it opens; it turns when it is open */}
            <span aria-hidden className="mr-1 inline-block transition-transform group-open:rotate-90">▸</span>
            Notes from Weave
          </summary>
          <pre className="mt-1.5 whitespace-pre-wrap break-words font-sans text-[12px] leading-relaxed text-muted-foreground">{promptRest}</pre>
        </details>
      ) : promptRest ? (
        <p
          className="mt-2 whitespace-pre-wrap text-[13px] leading-relaxed text-foreground"
          data-interaction-prompt-rest=""
        >
          {promptRest}
        </p>
      ) : null}
      {detail && <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{detail}</p>}

      {/*
        ── WHY, AND WHAT DEPENDS ON THE ANSWER (contracts 0.2.9) ─────────────────────────────────
        
        Three optional sentences, each a labelled line rather than a paragraph, in the order a reader
        decides in: why the agent could not resolve this itself, what changes with the answer, and
        what happens if they skip. Rendered only when present — a pre-0.2.9 producer sends none and
        the card looks exactly as it did.
      */}
      {(interaction.why || interaction.impact || interaction.assumption_if_unanswered) && (
        <dl className="mt-2 grid gap-1 text-[12px] leading-relaxed">
          {interaction.why && (
            <div className="flex gap-1.5">
              <dt className="shrink-0 font-medium text-foreground">Why I'm asking:</dt>
              <dd className="min-w-0 text-muted-foreground">{interaction.why}</dd>
            </div>
          )}
          {interaction.impact && (
            <div className="flex gap-1.5">
              <dt className="shrink-0 font-medium text-foreground">What it changes:</dt>
              <dd className="min-w-0 text-muted-foreground">{interaction.impact}</dd>
            </div>
          )}
          {interaction.assumption_if_unanswered && (
            <div className="flex gap-1.5">
              <dt className="shrink-0 font-medium text-foreground">If you skip this:</dt>
              <dd className="min-w-0 text-muted-foreground">{interaction.assumption_if_unanswered}</dd>
            </div>
          )}
        </dl>
      )}

      {/* WHAT HAPPENS NEXT — before the controls, so the reader commits knowing the outcome. */}
      {/*
        The consequence line matters when answering DOES something the reader should weigh — arms a
        deploy, raises a budget, starts a second run. On a `clarify` it read "Answering continues
        this run from where it paused — no new run is started", which is the standing chip's sentence
        in protocol words, one line below the standing chip.
      */}
      {consequence && (
        <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground" aria-live="polite">
          {consequence}
        </p>
      )}


      {interaction.context_ref?.id && onRevealContext && (
        <button
          type="button"
          onClick={() => onRevealContext(interaction.context_ref!)}
          className="mt-1.5 text-[12px] font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        >
          Show what this is about
        </button>
      )}

      {/*
        The kind-specific body, between the consequence and the controls: it is CONTEXT for the
        decision, so it belongs above the thing that makes the decision.
      */}
      <InteractionBody
        interaction={interaction}
        usage={usage}
        changeset={changeset}
        toolCall={toolCall}
        inputValues={inputValues}
        onInputChange={(field, value) => setInputValues((prev) => ({ ...prev, [field]: value }))}
      />

      {children}

      {/*
        A BATCH replaces the card's own controls with its sub-questions: the answer is `answers`, not
        an option (0.2.9). The single-question path below is untouched for everything else.
      */}
      {isBatch ? (
        <ClarifyQuestions
          questions={questions!}
          answers={answers}
          onChange={(questionId, answer) =>
            setAnswers((prev) => {
              const next = { ...prev };
              if (answer) next[questionId] = answer;
              else delete next[questionId];
              return next;
            })
          }
          disabled={busy}
          idPrefix={`ix-${interaction.interaction_id}`}
        />
      ) : (
        <>
          {interaction.options.length > 0 && actionMode && actionLayout === "pills" && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2" role="group" aria-label={copy.title}>
              {interaction.options.map((o) => (
                <Button
                  key={o.option_id}
                  type="button"
                  size="sm"
                  // the recommended action is the one filled button; the others are quiet (an outlined Decline in the
                  // accent colour read as a second primary next to Approve — dev 2026-11-02)
                  variant={o.recommended ? "default" : "secondary"}
                  disabled={busy}
                  aria-pressed={optionId === o.option_id ? true : undefined}
                  title={o.description ?? undefined}
                  data-interaction-option={o.option_id}
                  onClick={() => void pressOption(o.option_id)}
                >
                  {busy && optionId === o.option_id ? "Sending…" : o.label}
                </Button>
              ))}
            </div>
          )}
          {interaction.options.length > 0 && actionMode && actionLayout === "list" && (
            <ul className="mt-2.5 flex flex-col gap-1" role="group" aria-label={copy.title} data-interaction-options="list">
              {interaction.options.map((o) => {
                const chosen = optionId === o.option_id;
                return (
                  <li key={o.option_id}>
                    <button
                      type="button"
                      disabled={busy}
                      aria-pressed={chosen ? true : undefined}
                      data-interaction-option={o.option_id}
                      onClick={() => void pressOption(o.option_id)}
                      className={cn(
                        "group flex w-full items-start gap-2.5 rounded-md border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60",
                        o.recommended
                          ? "border-primary/45 bg-primary/5 hover:bg-primary/10"
                          : "border-border/70 bg-background hover:border-border hover:bg-muted/50",
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full border",
                          o.recommended ? "border-primary bg-primary" : "border-muted-foreground/60",
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] leading-snug text-foreground">
                          {busy && chosen ? "Sending…" : o.label}
                        </span>
                        {o.description && <span className="mt-0.5 block text-[12px] text-muted-foreground">{o.description}</span>}
                      </span>
                      {o.recommended && (
                        <span className="shrink-0 text-[11px] font-medium text-primary">Recommended</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {interaction.options.length > 0 && !actionMode && (
            <div className="mt-2.5">
              <OptionCards
                ariaLabel={copy.title}
                options={interaction.options}
                value={optionId}
                onChange={chooseOption}
                disabled={busy}
                idPrefix={`ix-${interaction.interaction_id}`}
                layout={optionLayout}
                disabledOptionIds={blockedOptionIds}
              />
              {approveBlocked === "validation_not_passed" && (
                <p className="mt-1.5 text-[12px] text-ma-warn-text" data-approve-blocked={approveBlocked}>
                  Approve unavailable: the package check did not pass. You can save the changes to a branch or decline.
                </p>
              )}
            </div>
          )}

          {/*
            Concrete org items to pick from (0.2.9 `candidates`), answered with `ref_id`. A second
            radio group under the options rather than merged into them: the options are the agent's
            interpretations, the candidates are the org's actual objects, and the reader should be
            able to tell which they are choosing.
          */}
          {!!interaction.candidates?.length && (
            <div className="mt-2.5">
              {interaction.options.length > 0 && (
                <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Or pick from your org
                </p>
              )}
              <OptionCards
                ariaLabel={`${interaction.prompt} — candidates`}
                options={candidatesAsOptions(interaction.candidates)}
                value={refId}
                onChange={chooseCandidate}
                disabled={busy}
                idPrefix={`ix-${interaction.interaction_id}-cand`}
              />
            </div>
          )}
        </>
      )}

      {/*
        FREE TEXT IS ALWAYS AVAILABLE, NOT ALWAYS ON SCREEN.
        
        `allow_free_text` is true on nearly every interaction, so a two-row textarea sat under every
        question the run ever asked — the single biggest block of the "form" reading, and unused
        whenever the offered options were right (which is most of the time, since one is
        recommended). Behind a disclosure it stays one click away and stops competing with the
        options for the reader's attention.
        
        `<details>` rather than state: it is the semantics the browser already has for this, it is
        keyboard-operable and announced without any ARIA of ours, and it needs no re-render. When
        there are NO options the textarea is the only way to answer, so it renders open.
      */}
      {freeTextOnly && <div className="mt-2.5">{freeTextDisclosure}</div>}

      {needsOrgName && (
        <div className="mt-2.5 rounded-md border border-ma-err-line bg-ma-err-soft p-2.5">
          <label
            htmlFor={`ix-${interaction.interaction_id}-org`}
            className="block text-[12px] font-medium text-foreground"
          >
            {/*
              §8.2 rule 12. The org name is required on production specifically, and typing it is the
              friction — a checkbox would not make anyone read the list above.
            */}
            This is a production org. Type <span className="font-semibold">{org?.org_name}</span> to
            confirm.
          </label>
          <input
            id={`ix-${interaction.interaction_id}-org`}
            value={orgNameTyped}
            onChange={(e) => setOrgNameTyped(e.target.value)}
            disabled={busy}
            autoComplete="off"
            className="mt-1.5 w-full rounded border border-border bg-background px-2 py-1 text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      )}

      {gated && (
        <div className="mt-2.5 rounded-md border border-border bg-muted/40 p-2.5">
          <p className="text-[12px] font-medium text-foreground">
            {unmetEvidence!.length === 1
              ? "One required check has not finished."
              : `${unmetEvidence!.length} required checks have not finished.`}
          </p>
          <ul className="mt-1 list-disc pl-4 text-[12px] text-muted-foreground">
            {unmetEvidence!.map((e) => (
              <li key={e.evidence_id}>{e.kind}</li>
            ))}
          </ul>
          {/*
            The override is its OWN labelled affordance, never the Approve button with a modifier
            (§5.4 N2 / §B3 gate 2). Opening it is a deliberate second act, and it demands a reason
            that is audited into `apply_log.approval`.
          */}
          {overrideOpen ? (
            <div className="mt-2">
              <label
                htmlFor={`ix-${interaction.interaction_id}-override`}
                className="block text-[12px] font-medium text-muted-foreground"
              >
                Why are you approving without these checks?
              </label>
              <Textarea
                id={`ix-${interaction.interaction_id}-override`}
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                maxLength={OVERRIDE_REASON_MAX}
                rows={2}
                disabled={busy}
                className="mt-1 text-[13px]"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                This reason is recorded with the approval.
              </p>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => setOverrideOpen(true)}
            >
              Approve without them anyway…
            </Button>
          )}
        </div>
      )}

      {/*
        ONE ROW FOR THE DECISION. The submit, the reason it is off, and the free-text disclosure
        share a line: the disclosure is a summary until opened, and `open:basis-full` drops it to a
        row of its own — under the button, full width — the moment it is.
      */}
      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
        {/*
          A VERB, never the chosen option's LABEL — and never one that contradicts the choice.
          
          Two wrong versions preceded this one:
          
            · `chosenLabel ?? copy.fallbackAffirmative` put a metadata name on the button
              ("Opportunity_Line__c"), which says what was picked rather than what pressing it does.
            · `copy.fallbackAffirmative` alone read **Approve** with "Cancel" selected, and submitted
              `{option_id:"reject"}` — a control stating the opposite of its effect. Seen in
              production on an `approve_changeset` card with "Validate only (checkOnly)" chosen.
          
          `submitVerb` resolves the affirmative verb for an affirmative option and a plain "Submit"
          for anything else, so it can neither lie nor wear a name. The label is therefore NOT a
          stable locator — hence `data-interaction-submit` below, which is.
        */}
        {(!actionMode || needsText || needsConfirm || freeText.trim() || gated) && (
        <Button
          type="button"
          size="sm"
          variant={needsConfirm ? "destructive" : undefined}
          disabled={!ready}
          onClick={submit}
          /*
            A STABLE HANDLE for tests and for the harness, because the visible verb legitimately
            changes with the selection (below) and with the pending state. Locating this button by
            its label is what let an earlier double-submit test pass while its extra clicks missed.
          */
          data-interaction-submit={interaction.interaction_id}
        >
          {busy ? "Sending…" : editsPlan ? "Edit plan" : submitVerb(interaction.kind, optionId, owner)}
        </Button>
        )}
        {/*
          Why the button is off, stated rather than left to be guessed. §10's rule that a disabled
          control explains itself — and the reason a reader does not conclude the card is broken.
        */}
        {!ready && !busy && (!actionMode || needsText || gated) && (
          <span className="text-[12px] text-muted-foreground">
            {!answerable && !formAnswered
              ? needsText
                ? "Describe the changes you want to continue."
                : interaction.kind === "provide_input"
                ? "Fill in the required fields to continue."
                : isBatch
                  ? "Answer the required questions to continue."
                  : interaction.options.length || interaction.candidates?.length
                    ? "Choose an option, or type an answer."
                    : "Type an answer to continue."
              : !orgNameOk
                ? "Type the org name to confirm."
                : "Add a reason to approve without the checks."}
          </span>
        )}

        {!freeTextOnly && freeTextDisclosure}
      </div>

      {/*
        `role="alert"` because this interrupts: the user just pressed a button and it did not do what
        they expected. §9's rule on errors that must interrupt.
      */}
      {error && (
        <p role="alert" className="mt-2 text-[12px] text-ma-err-text">
          {error}
        </p>
      )}
    </section>
  );
}
